import { useQuery } from "@tanstack/react-query";
import { useAppStore } from "../../store";

interface BillingStatus {
  subscriptionStatus: string;
  trialEndsAt?: string;
  subscriptionEndsAt?: string;
}

async function fetchBillingStatus(): Promise<BillingStatus> {
  const apiBase = import.meta.env.VITE_API_BASE ?? "";
  const token = useAppStore.getState().authToken;
  const res = await fetch(`${apiBase}/api/billing/status`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) return { subscriptionStatus: "trial" };
  return res.json();
}

export function BillingBanner() {
  const isAuthenticated = useAppStore((s) => s.isAuthenticated);
  const { data: billing } = useQuery({
    queryKey: ["billing-status"],
    queryFn: fetchBillingStatus,
    enabled: isAuthenticated && !!import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY,
    staleTime: 5 * 60 * 1000,
  });

  if (!billing || billing.subscriptionStatus === "active") return null;

  const trialEnd = billing.trialEndsAt ? new Date(billing.trialEndsAt) : null;
  const daysLeft = trialEnd ? Math.ceil((trialEnd.getTime() - Date.now()) / 86400000) : null;
  const expired = daysLeft !== null && daysLeft <= 0;

  const handleUpgrade = async () => {
    const apiBase = import.meta.env.VITE_API_BASE ?? "";
    const token = useAppStore.getState().authToken;
    const res = await fetch(`${apiBase}/api/billing/create-checkout`, {
      method: "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    const { url } = await res.json();
    if (url) window.location.href = url;
  };

  return (
    <div style={{
      textAlign: "center", padding: "6px 16px", fontSize: "12px",
      background: expired ? "rgba(239,68,68,0.15)" : "var(--color-bg-surface)",
      borderBottom: "1px solid var(--color-border-default)",
      color: expired ? "#f87171" : "var(--color-text-muted)",
      display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
    }}>
      <span>
        {expired
          ? "Your trial has expired."
          : daysLeft !== null
          ? `${daysLeft} day${daysLeft === 1 ? "" : "s"} left in your trial.`
          : "You're on a free trial."}
      </span>
      <button
        onClick={handleUpgrade}
        style={{
          color: "var(--color-accent)", background: "none", border: "none",
          cursor: "pointer", fontSize: "12px", textDecoration: "underline",
        }}
      >
        Upgrade
      </button>
    </div>
  );
}
